import { Link as MuiLink, type LinkProps as MuiLinkProps } from '@mui/material';
import { forwardRef } from 'react';

export type ExternalLinkProps = MuiLinkProps & {
  href?: string;
};

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

// Robust href guard using URL parsing.
// - Hash-only links ("#top") are allowed as-is.
// - Relative/absolute paths are resolved against current origin and then
//   allowed because they result in http(s) protocol.
// - Unsafe schemes (javascript:, data:, blob:, ftp:, file:, etc.) are blocked.
const sanitizeHref = (href?: string): string | undefined => {
  if (!href || typeof href !== 'string') return undefined;

  // Allow pure in-page anchors without normalization
  if (href.startsWith('#')) return href;

  try {
    // Use current origin as base to normalize relative URLs.
    // Fallback origin for non-browser environments just in case.
    const origin =
      (typeof window !== 'undefined' && window.location?.origin) ||
      'http://localhost';

    const url = new URL(href, origin);

    // If the normalized protocol isn't allowed, drop the href
    if (!ALLOWED_PROTOCOLS.has(url.protocol)) return undefined;

    // For site-relative paths and same-origin links, keep the original href
    // (so we don't turn "/docs" into "http://example.com/docs" in the markup)
    return href;
  } catch {
    return undefined;
  }
};

/**
 * - If target="_blank", always adds rel="noopener noreferrer".
 * - Strips unsafe href schemes.
 * - Works with i18next <Trans> placeholders.
 */
export const ExternalLink = forwardRef<HTMLAnchorElement, ExternalLinkProps>(
  ({ href, rel, target = '_blank', sx, ...rest }, ref) => {
    const safeHref = sanitizeHref(href);

    const normalizedRel =
      target === '_blank'
        ? Array.from(
            new Set(`${rel ?? ''} noopener noreferrer`.trim().split(/\s+/)),
          ).join(' ')
        : rel;
    return (
      <MuiLink
        ref={ref}
        href={safeHref}
        target={target}
        rel={normalizedRel}
        sx={{
          textDecorationColor: 'currentColor',
          textDecorationThickness: '0.1px', // Normal thickness
          '&:hover': {
            textDecorationThickness: '1px', // Thicker on hover
            textDecorationColor: 'currentColor', // Keep hover color consistent
          },
          ...sx,
        }}
        {...rest}
      />
    );
  },
);
